import { RoutesPaths } from "../../../constants/routes.ts"
import type { GuideQuestion } from "../types/guide-content.types.ts"

/**
 * Real, operational Q&A — every answer here describes behavior confirmed
 * against the actual production components/constants this session (see the
 * `docs/kb` decision this shipped under for the exact files checked). A
 * category with thinner real-UI coverage right now (Customers/
 * Subcontractors/Notifications/Settings) gets fewer, more conservative
 * entries rather than invented specifics — the same rule as every other
 * part of the Guide: never describe a feature, status, or field that isn't
 * really there.
 */
export const GUIDE_QUESTIONS: GuideQuestion[] = [
  // ---- Getting Started -----------------------------------------------
  {
    id: "gs-what-is-ezbuild",
    category: "getting-started",
    questionKey: "guide.qa.questions.gsWhatIsEzbuild.question",
    answerKey: "guide.qa.questions.gsWhatIsEzbuild.answer",
    relatedGuideId: "start-here-your-first-lead"
  },
  {
    id: "gs-lead-to-project",
    category: "getting-started",
    questionKey: "guide.qa.questions.gsLeadToProject.question",
    answerKey: "guide.qa.questions.gsLeadToProject.answer",
    relatedGuideId: "start-here-lead-to-project",
    relatedGuideStepId: "lead-to-project",
    keywords: ["workflow", "pipeline", "won"]
  },

  // ---- Leads ------------------------------------------------------------
  {
    id: "leads-how-create",
    category: "leads",
    questionKey: "guide.qa.questions.leadsHowCreate.question",
    answerKey: "guide.qa.questions.leadsHowCreate.answer",
    relatedRoute: RoutesPaths.lead.add,
    relatedGuideId: "leads-what-is-a-lead"
  },
  {
    id: "leads-stage-vs-operational-state",
    category: "leads",
    questionKey: "guide.qa.questions.leadsStageVsState.question",
    answerKey: "guide.qa.questions.leadsStageVsState.answer",
    relatedGuideId: "leads-stages",
    keywords: ["hot", "follow-up", "status"]
  },
  {
    id: "leads-schedule-follow-up",
    category: "leads",
    questionKey: "guide.qa.questions.leadsScheduleFollowUp.question",
    answerKey: "guide.qa.questions.leadsScheduleFollowUp.answer",
    relatedGuideId: "leads-overview",
    relatedGuideStepId: "lead-overview"
  },
  {
    id: "leads-what-is-hot",
    category: "leads",
    questionKey: "guide.qa.questions.leadsWhatIsHot.question",
    answerKey: "guide.qa.questions.leadsWhatIsHot.answer",
    relatedGuideId: "leads-stages"
  },
  {
    id: "leads-mark-lost",
    category: "leads",
    questionKey: "guide.qa.questions.leadsMarkLost.question",
    answerKey: "guide.qa.questions.leadsMarkLost.answer",
    relatedGuideId: "leads-stages",
    keywords: ["closed", "archive"]
  },
  {
    id: "leads-lost-leads-go",
    category: "leads",
    questionKey: "guide.qa.questions.leadsLostGoWhere.question",
    answerKey: "guide.qa.questions.leadsLostGoWhere.answer",
    relatedGuideId: "leads-stages",
    keywords: ["archive"]
  },
  {
    id: "leads-create-proposal",
    category: "leads",
    questionKey: "guide.qa.questions.leadsCreateProposal.question",
    answerKey: "guide.qa.questions.leadsCreateProposal.answer",
    relatedGuideId: "leads-proposals",
    relatedGuideStepId: "lead-sales-flow"
  },
  {
    id: "leads-create-contract",
    category: "leads",
    questionKey: "guide.qa.questions.leadsCreateContract.question",
    answerKey: "guide.qa.questions.leadsCreateContract.answer",
    relatedGuideId: "leads-contracts",
    relatedGuideStepId: "lead-sales-flow"
  },
  {
    id: "leads-becomes-project",
    category: "leads",
    questionKey: "guide.qa.questions.leadsBecomesProject.question",
    answerKey: "guide.qa.questions.leadsBecomesProject.answer",
    relatedGuideId: "leads-converting-to-project",
    relatedGuideStepId: "lead-to-project",
    keywords: ["won", "convert"]
  },

  // ---- Projects -----------------------------------------------------
  {
    id: "projects-how-create",
    category: "projects",
    questionKey: "guide.qa.questions.projectsHowCreate.question",
    answerKey: "guide.qa.questions.projectsHowCreate.answer",
    relatedGuideId: "projects-understanding-projects"
  },
  {
    id: "projects-what-is-snapshot",
    category: "projects",
    questionKey: "guide.qa.questions.projectsWhatIsSnapshot.question",
    answerKey: "guide.qa.questions.projectsWhatIsSnapshot.answer",
    relatedGuideId: "projects-understanding-projects",
    relatedGuideStepId: "project-overview"
  },
  {
    id: "projects-how-edit",
    category: "projects",
    questionKey: "guide.qa.questions.projectsHowEdit.question",
    answerKey: "guide.qa.questions.projectsHowEdit.answer",
    relatedGuideId: "projects-understanding-projects"
  },
  {
    id: "projects-change-dates",
    category: "projects",
    questionKey: "guide.qa.questions.projectsChangeDates.question",
    answerKey: "guide.qa.questions.projectsChangeDates.answer",
    relatedGuideId: "projects-understanding-projects"
  },

  // ---- Milestones -----------------------------------------------------
  {
    id: "milestones-what-is",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesWhatIs.question",
    answerKey: "guide.qa.questions.milestonesWhatIs.answer",
    relatedGuideId: "projects-milestones",
    relatedGuideStepId: "project-milestones"
  },
  {
    id: "milestones-status-mean",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesStatusMean.question",
    answerKey: "guide.qa.questions.milestonesStatusMean.answer",
    relatedGuideId: "projects-milestones",
    relatedGuideStepId: "project-milestones",
    keywords: ["planned", "in progress", "completed", "blocked", "pending"]
  },
  {
    id: "milestones-risk-mean",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesRiskMean.question",
    answerKey: "guide.qa.questions.milestonesRiskMean.answer",
    relatedGuideId: "projects-milestones",
    keywords: ["on track", "at risk", "high risk"]
  },
  {
    id: "milestones-dependencies",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesDependencies.question",
    answerKey: "guide.qa.questions.milestonesDependencies.answer",
    relatedGuideId: "projects-milestones"
  },
  {
    id: "milestones-table-vs-gantt",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesTableVsGantt.question",
    answerKey: "guide.qa.questions.milestonesTableVsGantt.answer",
    relatedGuideId: "projects-milestones"
  },
  {
    id: "milestones-receivables-payables",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesReceivablesPayables.question",
    answerKey: "guide.qa.questions.milestonesReceivablesPayables.answer",
    relatedGuideId: "projects-milestones",
    relatedGuideStepId: "project-milestones",
    keywords: ["settled", "planned", "committed"]
  },
  {
    id: "milestones-add-financial-item",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesAddFinancialItem.question",
    answerKey: "guide.qa.questions.milestonesAddFinancialItem.answer",
    relatedGuideId: "projects-milestones"
  },
  {
    id: "milestones-affect-finance",
    category: "milestones",
    questionKey: "guide.qa.questions.milestonesAffectFinance.question",
    answerKey: "guide.qa.questions.milestonesAffectFinance.answer",
    relatedGuideId: "projects-finance",
    relatedGuideStepId: "project-finance",
    keywords: ["connected", "connection"]
  },

  // ---- Finance --------------------------------------------------------
  {
    id: "finance-how-works",
    category: "finance",
    questionKey: "guide.qa.questions.financeHowWorks.question",
    answerKey: "guide.qa.questions.financeHowWorks.answer",
    relatedGuideId: "projects-finance",
    relatedGuideStepId: "project-finance"
  },
  {
    id: "finance-lines-come-from",
    category: "finance",
    questionKey: "guide.qa.questions.financeLinesComeFrom.question",
    answerKey: "guide.qa.questions.financeLinesComeFrom.answer",
    relatedGuideId: "projects-finance",
    relatedGuideStepId: "project-finance"
  },
  {
    id: "finance-what-is-receivable",
    category: "finance",
    questionKey: "guide.qa.questions.financeWhatIsReceivable.question",
    answerKey: "guide.qa.questions.financeWhatIsReceivable.answer",
    relatedGuideId: "projects-finance"
  },
  {
    id: "finance-what-is-payable",
    category: "finance",
    questionKey: "guide.qa.questions.financeWhatIsPayable.question",
    answerKey: "guide.qa.questions.financeWhatIsPayable.answer",
    relatedGuideId: "projects-finance"
  },
  {
    id: "finance-record-payment",
    category: "finance",
    questionKey: "guide.qa.questions.financeRecordPayment.question",
    answerKey: "guide.qa.questions.financeRecordPayment.answer",
    relatedGuideId: "projects-finance",
    relatedGuideStepId: "project-finance"
  },
  {
    id: "finance-statuses-mean",
    category: "finance",
    questionKey: "guide.qa.questions.financeStatusesMean.question",
    answerKey: "guide.qa.questions.financeStatusesMean.answer",
    relatedGuideId: "projects-finance",
    keywords: ["planned", "pending", "completed", "cancelled", "partial", "new"]
  },
  {
    id: "finance-milestones-connection",
    category: "finance",
    questionKey: "guide.qa.questions.financeMilestonesConnection.question",
    answerKey: "guide.qa.questions.financeMilestonesConnection.answer",
    relatedGuideId: "projects-milestones",
    relatedGuideStepId: "project-milestones"
  },
  {
    id: "finance-summary-shows",
    category: "finance",
    questionKey: "guide.qa.questions.financeSummaryShows.question",
    answerKey: "guide.qa.questions.financeSummaryShows.answer",
    relatedGuideId: "projects-finance"
  },

  // ---- Invoices -----------------------------------------------------
  {
    id: "invoices-how-create",
    category: "invoices",
    questionKey: "guide.qa.questions.invoicesHowCreate.question",
    answerKey: "guide.qa.questions.invoicesHowCreate.answer",
    relatedGuideId: "projects-invoices",
    relatedGuideStepId: "project-invoices"
  },
  {
    id: "invoices-statuses-mean",
    category: "invoices",
    questionKey: "guide.qa.questions.invoicesStatusesMean.question",
    answerKey: "guide.qa.questions.invoicesStatusesMean.answer",
    relatedGuideId: "projects-invoices",
    keywords: [
      "pending",
      "paid",
      "overdue",
      "draft",
      "issued",
      "partially paid",
      "void"
    ]
  },
  {
    id: "invoices-record-payment",
    category: "invoices",
    questionKey: "guide.qa.questions.invoicesRecordPayment.question",
    answerKey: "guide.qa.questions.invoicesRecordPayment.answer",
    relatedGuideId: "projects-invoices"
  },
  {
    id: "invoices-know-unpaid",
    category: "invoices",
    questionKey: "guide.qa.questions.invoicesKnowUnpaid.question",
    answerKey: "guide.qa.questions.invoicesKnowUnpaid.answer",
    relatedGuideId: "projects-invoices",
    keywords: ["outstanding", "balance"]
  },
  {
    id: "invoices-project-connection",
    category: "invoices",
    questionKey: "guide.qa.questions.invoicesProjectConnection.question",
    answerKey: "guide.qa.questions.invoicesProjectConnection.answer",
    relatedGuideId: "projects-invoices",
    keywords: ["generated", "finance line"]
  },

  // ---- Tasks (Checklists) ---------------------------------------------
  {
    id: "tasks-how-create",
    category: "tasks",
    questionKey: "guide.qa.questions.tasksHowCreate.question",
    answerKey: "guide.qa.questions.tasksHowCreate.answer",
    relatedGuideId: "projects-tasks"
  },
  {
    id: "tasks-how-assign",
    category: "tasks",
    questionKey: "guide.qa.questions.tasksHowAssign.question",
    answerKey: "guide.qa.questions.tasksHowAssign.answer",
    relatedGuideId: "projects-tasks"
  },
  {
    id: "tasks-connect-milestone",
    category: "tasks",
    questionKey: "guide.qa.questions.tasksConnectMilestone.question",
    answerKey: "guide.qa.questions.tasksConnectMilestone.answer",
    relatedGuideId: "projects-tasks"
  },
  {
    id: "tasks-statuses-work",
    category: "tasks",
    questionKey: "guide.qa.questions.tasksStatusesWork.question",
    answerKey: "guide.qa.questions.tasksStatusesWork.answer",
    relatedGuideId: "projects-tasks",
    keywords: ["not started", "in progress", "done", "priority"]
  },

  // ---- Files ----------------------------------------------------------
  {
    id: "files-how-upload",
    category: "files",
    questionKey: "guide.qa.questions.filesHowUpload.question",
    answerKey: "guide.qa.questions.filesHowUpload.answer",
    relatedGuideId: "projects-files",
    relatedGuideStepId: "project-files"
  },
  {
    id: "files-what-are-albums",
    category: "files",
    questionKey: "guide.qa.questions.filesWhatAreAlbums.question",
    answerKey: "guide.qa.questions.filesWhatAreAlbums.answer",
    relatedGuideId: "projects-files"
  },
  {
    id: "files-document-type",
    category: "files",
    questionKey: "guide.qa.questions.filesDocumentType.question",
    answerKey: "guide.qa.questions.filesDocumentType.answer",
    relatedGuideId: "projects-files"
  },
  {
    id: "files-review-works",
    category: "files",
    questionKey: "guide.qa.questions.filesReviewWorks.question",
    answerKey: "guide.qa.questions.filesReviewWorks.answer",
    relatedGuideId: "projects-files"
  },
  {
    id: "files-assign-reviewer",
    category: "files",
    questionKey: "guide.qa.questions.filesAssignReviewer.question",
    answerKey: "guide.qa.questions.filesAssignReviewer.answer",
    relatedGuideId: "projects-files"
  },
  {
    id: "files-versioning",
    category: "files",
    questionKey: "guide.qa.questions.filesVersioning.question",
    answerKey: "guide.qa.questions.filesVersioning.answer",
    relatedGuideId: "projects-files"
  },
  {
    id: "files-share-customer",
    category: "files",
    questionKey: "guide.qa.questions.filesShareCustomer.question",
    answerKey: "guide.qa.questions.filesShareCustomer.answer",
    relatedGuideId: "projects-files"
  },

  // ---- Calendar -------------------------------------------------------
  {
    id: "calendar-schedule-meeting",
    category: "calendar",
    questionKey: "guide.qa.questions.calendarScheduleMeeting.question",
    answerKey: "guide.qa.questions.calendarScheduleMeeting.answer",
    relatedRoute: "/calendar"
  },
  {
    id: "calendar-reminders-work",
    category: "calendar",
    questionKey: "guide.qa.questions.calendarRemindersWork.question",
    answerKey: "guide.qa.questions.calendarRemindersWork.answer",
    relatedRoute: "/calendar"
  },
  {
    id: "calendar-who-notified",
    category: "calendar",
    questionKey: "guide.qa.questions.calendarWhoNotified.question",
    answerKey: "guide.qa.questions.calendarWhoNotified.answer",
    relatedRoute: "/calendar"
  }
]
